import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AssetScanner, { canScan } from '../components/AssetScanner';
import AssetForm, { labelsFor } from '../components/AssetForm';
import { cameFrom } from '../components/asset/BackLink';
import TagChip from '../components/asset/TagChip';
import { StockTakeCount, StockTakeHub } from '../components/StockTake';
import {
  deviceName, holderLabel, LIFECYCLE_LABEL, lifecycleTone, prefillFromCode, refreshAssetMeta, useAssetMeta
} from '../lib/assets';
import '../components/Assets.css';
import './AssetLookup.css';

/**
 * Lookup: what is this code? (Gordon, 28 October 2026)
 *
 * On a phone the camera opens at once and reads a Jarvis QR label or an
 * ordinary barcode. A USB or Bluetooth scanner types the code and presses
 * Enter into the box, which stays focused: scan after scan, no touching the
 * screen. A device label, a shelf label, a tag, a serial number, a MAC
 * address or a hostname all work - the server matches exactly and says what
 * it found (GET /assets/lookup).
 *
 * STOCK TAKE (29 October 2026): a second tab, where one or more people count
 * a place together - see components/StockTake.js. ?stocktake=<id> opens a
 * count, ?count=<place> joins or starts the count of that place (the old
 * /assets/stocktake/<place> address and shelf labels land here).
 *
 * ADDING FROM HERE (Gordon, 4 November): "Add to the register" on a code
 * nothing matched opens the add form here, started with what was scanned -
 * a tag as the tag, a serial as the serial, a MAC as the MAC (a web address
 * as nothing) - and "Add a new device" is always offered, both only to
 * somebody who may add. A device opened from here says "← Look up".
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const KEEP = 10;

const KIND_WORD = { tag: 'tag', serial: 'serial number', mac: 'MAC address', hostname: 'hostname', url: 'label' };

/** A phone, or anything held rather than sat at: the camera opens by itself there. */
const handheld = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 768px) and (pointer: coarse)').matches;

/** "3 days ago", for when a device last reported. */
function ago(iso) {
  const t = new Date(iso).getTime();
  if (!iso || Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 2) return 'just now';
  if (mins < 120) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hours ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function Card({ a }) {
  const gone = ['retired', 'disposed'].includes(a.lifecycle_status);
  const holder = holderLabel(a);
  const seen = ago(a.last_seen_at);
  return (
    <div className="al-card" data-testid="al-card" data-gone={gone}>
      <span className="al-ring" data-tone={lifecycleTone(a.lifecycle_status)}
        title={LIFECYCLE_LABEL[a.lifecycle_status] || ''} />
      <div className="al-body">
        <div className="al-name">{deviceName(a)} <TagChip asset={a} /></div>
        <ul className="al-lines">
          <li>{[a.category_name, LIFECYCLE_LABEL[a.lifecycle_status]].filter(Boolean).join(' · ')}</li>
          {a.serial_number && <li>Serial {a.serial_number}</li>}
          {holder && <li>With {holder}</li>}
          {a.location_path && <li>At {a.location_path}</li>}
          {seen && <li>Last seen {seen}</li>}
          {a.last_counted?.at && (
            <li data-testid="al-counted">
              Last counted {new Date(a.last_counted.at).toLocaleDateString('en-GB')}{a.last_counted.by_name ? ` by ${a.last_counted.by_name}` : ''}
            </li>
          )}
        </ul>
        {gone && <p className="al-flag" role="status">This device is {LIFECYCLE_LABEL[a.lifecycle_status].toLowerCase()}. It is not in service.</p>}
        <div className="al-actions">
          <Link className="btn btn-primary btn-small" to={`/assets/${a.id}`} state={cameFrom('Look up')}>Open</Link>
          {!gone && <Link className="btn btn-secondary btn-small" to={`/assets/${a.id}`} state={cameFrom('Look up')} data-action="assign">Assign / Move</Link>}
          {!gone && <Link className="btn btn-secondary btn-small" to={`/create?asset=${a.id}`} data-action="report">Report a problem</Link>}
        </div>
      </div>
    </div>
  );
}

function LookupPane() {
  const navigate = useNavigate();
  const meta = useAssetMeta();
  const canAdd = Boolean(meta?.access?.write);
  // The add form, when open: { prefill } (an empty prefill for "Add a new device").
  const [adding, setAdding] = useState(null);
  const added = useCallback((saved, then) => {
    setAdding(null);
    refreshAssetMeta();
    if (then === 'print') navigate(...labelsFor(saved));
    else navigate(`/assets/${saved.id}`, { state: cameFrom('Look up') });
  }, [navigate]);
  const input = useRef(null);
  const seq = useRef(0);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shown, setShown] = useState(null); // { code, data }
  const [history, setHistory] = useState([]);
  const [scanning, setScanning] = useState(() => canScan() && handheld());

  const refocus = useCallback(() => { setTimeout(() => input.current?.focus(), 0); }, []);
  useEffect(() => { if (!scanning) input.current?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = useCallback(async (raw) => {
    const text = String(raw || '').trim();
    if (!text) return;
    const mine = seq.current + 1;
    seq.current = mine;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_BASE}/assets/lookup`, { params: { code: text } });
      if (mine !== seq.current) return;
      if (data.kind === 'place' && data.place) {
        navigate(`/assets/place/${data.place.id}`);
        return;
      }
      setShown({ code: text, data });
      const first = data.assets?.[0];
      const label = data.kind === 'asset' ? `${first.asset_tag || deviceName(first)}`
        : data.kind === 'many' ? `${data.assets.length} devices`
          : data.kind === 'inbox' ? 'In the Discovery inbox' : 'Not in the register';
      setHistory((h) => [{ key: `${Date.now()}-${h.length}`, code: text, label, kind: data.kind, at: new Date() }, ...h].slice(0, KEEP));
    } catch (e) {
      if (mine !== seq.current) return;
      setShown(null);
      setError(e.response?.data?.error || 'Could not look that up. Try again.');
    } finally {
      if (mine === seq.current) setBusy(false);
      setCode('');
      refocus();
    }
  }, [navigate, refocus]);

  const onRaw = useCallback((found) => {
    setScanning(false);
    lookup(found);
    return true;
  }, [lookup]);
  const noop = useCallback(() => {}, []);

  const data = shown?.data;
  const assets = data?.assets || [];

  return (
    <>
      <h1 className="page-title">Look up a device</h1>
      <p className="as-quiet">Scan a label or barcode, or type a tag, serial number, MAC address or hostname.</p>
      {canAdd && (
        <p className="al-addnew-row">
          <button type="button" className="al-addnew" data-testid="al-add-new" onClick={() => setAdding({ prefill: null })}>
            Add a new device
          </button>
        </p>
      )}

      {scanning && canScan()
        ? <AssetScanner barcodes onTag={noop} onRaw={onRaw} onCancel={() => { setScanning(false); refocus(); }} />
        : canScan() && (
          <button type="button" className="btn btn-primary al-camera" data-testid="al-camera"
            onClick={() => setScanning(true)}>
            {shown ? 'Scan another' : 'Scan with the camera'}
          </button>
        )}

      <form className="al-form" onSubmit={(e) => { e.preventDefault(); lookup(code); }}>
        <label htmlFor="al-code" className="al-sr">Code</label>
        <input id="al-code" ref={input} type="text" value={code} autoComplete="off" autoCapitalize="none"
          spellCheck={false} placeholder="Scan or type a code, then Enter"
          onChange={(e) => setCode(e.target.value)} />
        <button type="submit" className="btn btn-secondary" disabled={busy || !code.trim()}>Look up</button>
      </form>

      {error && <p className="as-error" role="alert">{error}</p>}

      {data && (
        <section className="al-result" aria-live="polite" data-testid="al-result" data-kind={data.kind}>
          {data.kind === 'asset' && <Card a={assets[0]} />}
          {data.kind === 'many' && (
            <>
              <h2>{assets.length} devices match {shown.code}</h2>
              {assets.map((a) => <Card key={a.id} a={a} />)}
            </>
          )}
          {data.kind === 'inbox' && (
            <div className="al-inbox" data-testid="al-inbox">
              <h2>Found in the Discovery inbox</h2>
              <p>
                {[data.discovery.manufacturer, data.discovery.model].filter(Boolean).join(' ') || data.discovery.kind}
                {' '}was seen on the network but is not in the register yet.
              </p>
              <Link className="btn btn-primary btn-small" to="/assets/discovery">Accept it in the inbox</Link>
            </div>
          )}
          {data.kind === 'none' && (
            <div className="al-none" data-testid="al-none">
              <h2>Not in the register</h2>
              <p>Nothing matches {shown.code}{KIND_WORD[data.code_kind] ? ` as a ${KIND_WORD[data.code_kind]}` : ''}.</p>
              {canAdd && (
                <button type="button" className="btn btn-primary btn-small" data-action="add"
                  onClick={() => setAdding({ prefill: prefillFromCode(shown.code, data.code_kind) })}>
                  Add to the register
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {history.length > 0 && (
        <section className="al-history" data-testid="al-history">
          <h2>Scanned just now</h2>
          <ul>
            {history.map((h) => (
              <li key={h.key}>
                <button type="button" onClick={() => lookup(h.code)}>
                  <span>{h.label}</span>
                  <span className="as-quiet">{h.code}</span>
                  <span className="al-when">{h.at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {adding && (
        <AssetForm meta={meta} prefill={adding.prefill} onClose={() => { setAdding(null); refocus(); }} onSaved={added} />
      )}
    </>
  );
}

export default function AssetLookup() {
  const [params, setParams] = useSearchParams();
  const stocktake = params.get('stocktake');
  const countAt = params.get('count');
  const stock = Boolean(stocktake || countAt || params.get('mode') === 'stock');
  const [notice, setNotice] = useState(null);
  const openCount = useCallback((id, text) => {
    setNotice(text || null);
    setParams({ stocktake: id });
  }, [setParams]);

  return (
    <div className="container as-page al-page">
      <nav className="al-tabs" aria-label="Lookup">
        <Link to="/assets/lookup" aria-current={stock ? undefined : 'page'}>Look up</Link>
        <Link to="/assets/lookup?mode=stock" aria-current={stock ? 'page' : undefined}>Stock take</Link>
      </nav>
      {!stock && <LookupPane />}
      {stock && !stocktake && (
        <>
          <h1 className="page-title">Stock take</h1>
          <StockTakeHub placeId={countAt} onOpen={openCount} />
        </>
      )}
      {stocktake && <StockTakeCount key={stocktake} id={stocktake} notice={notice} />}
    </div>
  );
}
